import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, TreePine } from 'lucide-react';

const ProtectedRoute = ({ children, requiredTier = null, requiredRole = null }) => {
  const { user, isLoading, isAuthenticated, checkAuth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // If user data was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verify = async () => {
      await checkAuth();
      setIsChecking(false);
    };
    verify();
  }, [checkAuth, location.state]);

  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 bg-emerald-200 rounded-full animate-ping opacity-25" />
            <div className="relative w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center">
              <TreePine className="w-12 h-12 text-emerald-500" />
            </div>
          </div>
          <Loader2 className="w-8 h-8 text-coral-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading your adventure...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check tier requirements
  if (requiredTier) {
    const tierLevels = { free: 0, basic: 1, premium: 2 };
    const userTierLevel = tierLevels[user?.tier] || 0;
    const requiredTierLevel = tierLevels[requiredTier] || 0;
    
    if (userTierLevel < requiredTierLevel) {
      return <Navigate to="/upgrade" state={{ required: requiredTier }} replace />;
    }
  }

  // Check role requirements
  if (requiredRole) {
    const roleHierarchy = ['free', 'basic', 'premium', 'community_manager', 'super_admin'];
    const userRoleIndex = roleHierarchy.indexOf(user?.role);
    const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);
    
    if (userRoleIndex < requiredRoleIndex) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
